import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { HelpButton } from '../components/HelpButton.js';
import { PageTitle } from '../components/Layouts.js';
import { SetupPanel } from '../components/SetupPanel.js';
import { UptimeChart } from '../components/UptimeChart.js';
import { Sparkline } from '../components/charts.js';
import { FullRescan } from '../components/FullRescan.js';
import { ImprovementItem, useImprovements } from '../components/Improvements.js';
import { GradeBadge, TileStatusIcon, useLocale } from '../components/site.js';
import { Card, LoadError, Loading, Notice, SeverityBadge, StatusBadge } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';

type Range = '24h' | '7d' | '30d';
const RANGES: Range[] = ['24h', '7d', '30d'];
/** S3: Health Score, seven tiles, "what matters now" and the uptime chart. */
export function SiteDashboard() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const me = useMe();
  const timeZone = me.data?.account.timezone;
  const [range, setRange] = useState<Range>('24h');
  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
    refetchInterval: 30_000,
  });
  const uptime = useQuery({
    queryKey: ['site', siteId, 'uptime', range],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/uptime', { params: { path: { siteId }, query: { range } } })),
    refetchInterval: 60_000,
  });

  if (overview.isPending) return <Loading />;
  if (overview.isError)
    return (
      <LoadError
        message={errorMessage(overview.error, t)}
        onRetry={() => void overview.refetch()}
      />
    );
  const o = overview.data;
  const incident = o.openIncident;
  const unreachable = (location.state as { unreachable?: boolean } | null)?.unreachable;
  const trend = o.health?.trend7d ?? null;

  return (
    <>
      <PageTitle
        actions={
          <StatusBadge
            state={
              o.site.status === 'paused'
                ? 'paused'
                : o.access.block === 'bot_protection'
                  ? 'setup'
                  : o.availability.state
            }
          />
        }
      >
        {o.site.name}
      </PageTitle>
      <p className="-mt-4 mb-4 break-all text-slate-700">{o.site.url}</p>

      <div className="mb-6 flex flex-col gap-3">
        <SetupPanel
          siteId={siteId}
          access={o.access}
          lastCheckAt={o.availability.lastCheckAt}
          timeZone={timeZone}
        />
        {unreachable ? <Notice kind="warning">{t('addSite.unreachable')}</Notice> : null}
        {incident && incident.kind === 'downtime' ? (
          <Notice kind="error">
            {t('site.openIncident', {
              time: formatDateTime(incident.startedAt, i18n.language, timeZone),
            })}
          </Notice>
        ) : null}
        {o.availability.lastCheckAt ? null : <Notice kind="info">{t('site.notChecked')}</Notice>}
        <FullRescan siteId={siteId} />
      </div>

      <Card className="mb-6">
        <h2 className="mb-3 text-xl font-semibold">{t('health.title')}</h2>
        {o.health ? (
          <div className="flex flex-wrap items-center gap-4">
            <GradeBadge grade={o.health.grade} score={o.health.score} />
            <div>
              <p className="text-lg font-semibold">{o.health.label}</p>
              {trend !== null ? (
                <p className="text-sm text-slate-700">
                  {trend === 0
                    ? t('health.trendSame')
                    : t(trend > 0 ? 'health.trendUp' : 'health.trendDown', {
                        points: Math.abs(trend),
                      })}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-slate-700">
            {t(o.access.block === 'bot_protection' ? 'health.hidden' : 'health.pending')}
          </p>
        )}
      </Card>

      <GoodNews siteId={siteId} timeZone={timeZone} />

      <section aria-labelledby="top-title" className="mb-6">
        <h2 id="top-title" className="mb-3 text-xl font-semibold">
          {t('site.top.title')}
        </h2>
        {o.topFindings.length === 0 ? (
          incident && incident.kind === 'downtime' ? (
            <Notice kind="warning">{t('site.top.down')}</Notice>
          ) : (
            <Notice kind="success">{t('site.top.empty')}</Notice>
          )
        ) : (
          <ol className="flex flex-col gap-2">
            {o.topFindings.map((f) => (
              <li
                key={f.id}
                className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <SeverityBadge severity={f.severity} />
                  <Link
                    to={`/sites/${siteId}/findings?open=${encodeURIComponent(f.ruleCode)}`}
                    className="font-medium text-blue-900 underline"
                  >
                    {f.title}
                  </Link>
                </span>
                {f.service ? (
                  <span className="shrink-0">
                    <HelpButton
                      siteId={siteId}
                      service={f.service}
                      findingId={f.id}
                      ruleCode={f.ruleCode}
                    />
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section aria-labelledby="tiles-title" className="mb-6">
        <h2 id="tiles-title" className="sr-only">
          {t('site.tiles.label')}
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {o.tiles.map((tile) => (
            <li key={tile.key}>
              <Card className="flex h-full flex-col">
                <h3 className="mb-2 text-lg font-semibold">
                  <Link to={`/sites/${siteId}/area/${tile.key}`} className="underline">
                    {t(`site.tiles.${tile.key}`)}
                  </Link>
                </h3>
                <div className="flex items-center justify-between gap-2">
                  <TileStatusIcon status={tile.status} />
                  <Sparkline values={tile.trend} />
                </div>
                <p className="mt-2 font-medium">{tile.headline}</p>
                {tile.sub ? <p className="mt-1 text-sm text-slate-700">{tile.sub}</p> : null}
                <p className="mt-2 text-sm text-slate-700">{tile.why}</p>
                <p className="mt-auto pt-2">
                  <Link
                    to={`/sites/${siteId}/area/${tile.key}`}
                    className="text-sm font-semibold text-blue-800 underline"
                  >
                    {t('site.tiles.more')}
                  </Link>
                </p>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">{t('site.chart.title')}</h2>
          <div role="group" aria-label={t('site.chart.range')} className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={range === r}
                onClick={() => setRange(r)}
                className={`min-h-11 rounded-md border px-3 font-semibold ${range === r ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
              >
                {t(`site.chart.ranges.${r}`)}
              </button>
            ))}
          </div>
        </div>
        {uptime.isPending ? <Loading /> : null}
        {uptime.isError ? (
          <LoadError
            message={errorMessage(uptime.error, t)}
            onRetry={() => void uptime.refetch()}
          />
        ) : null}
        {uptime.data ? (
          <UptimeChart
            points={uptime.data.series}
            uptimePct={uptime.data.uptimePct}
            timeZone={timeZone}
          />
        ) : null}
        {range === '30d' ? (
          <p className="mt-2 text-sm text-slate-700">{t('site.chart.retention')}</p>
        ) : null}
        <p className="mt-4">
          <Link to={`/sites/${siteId}/incidents`} className="font-semibold text-blue-800 underline">
            {t('site.incidentsLink')}
          </Link>
        </p>
      </Card>
    </>
  );
}

/** The good news of the last 7 days on top of the dashboard (T-36); hidden while there is none. */
function GoodNews({ siteId, timeZone }: { siteId: string; timeZone?: string | undefined }) {
  const { t } = useTranslation();
  const feed = useImprovements(siteId, 7);
  const items = feed.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section aria-labelledby="good-news-title" className="mb-6">
      <h2 id="good-news-title" className="mb-3 text-xl font-semibold">
        {t('changes.weekTitle')}
      </h2>
      <ul className="flex flex-col gap-2">
        {items.slice(0, 3).map((item) => (
          <li key={`${item.kind}-${item.at}-${item.title}`}>
            <ImprovementItem item={item} timeZone={timeZone} />
          </li>
        ))}
      </ul>
      <p className="mt-3">
        <Link to={`/sites/${siteId}/changes`} className="font-semibold text-blue-800 underline">
          {t('changes.all')}
        </Link>
      </p>
    </section>
  );
}
